import { askGroq } from "../../ai/groqClient";
import { applyYearGuard } from "./planGuards";
import { describeContext } from "./conversationContext";

// The planner only ever sees the dataset SCHEMA (column names/types/roles),
// never the actual rows. It converts a natural-language question into a
// JSON step plan that the local analytics engine (executePlan.js) executes
// against the full dataset in-browser.
export async function planQuery(question, schema, currentChart = null, conversationContext = null) {

  const prompt = `
You are the query planner for InsightPulse AI.

Your ONLY task is to convert a natural language question into a JSON execution
plan for a LOCAL analytics engine. You never see the actual data rows, only
the schema below — you never answer the question yourself.

Return ONLY JSON. No prose, no markdown fences.

If the question is NOT actually asking for data/calculation — e.g. greetings,
thanks, small talk, or a meta question about the conversation itself —
return { "steps": [] } and nothing else. Do not force an analysis step onto
a message that isn't asking for one.

Steps run in order and chain together: "transform" steps narrow/reshape the
working dataset, and at most one "terminal" step computes the final result
from whatever the working dataset looks like at that point.

TRANSFORM STEPS (can be combined, in any order that makes sense)

- filter: { "tool":"filter", "column":"<col>", "operator":"="|">"|"<"|">="|"<="|"contains", "value":<val>, "datePart":"year"|"quarter"|"month"|"day" }
    "datePart" is OPTIONAL. Use it only when <col> is a full date/time column
    (listed in schema.time) and you need to match part of it — e.g. filtering
    a "2013-04-12"-style column by year. Do NOT invent a column that isn't in
    the schema (like a standalone "Year" column) if it doesn't exist — instead
    filter the real date column with datePart:"year".
- groupBy: { "tool":"groupBy", "dimension":"<categorical col>", "measure":"<numeric col>", "aggregation":"sum"|"average"|"count"|"maximum"|"minimum" }
    turns the working data into rows of { group, value, rows }
- sort: { "tool":"sort", "by":"<col or 'value' if data was just grouped>", "direction":"asc"|"desc" }
- top: { "tool":"top", "n":<int>, "by":"<optional col/'value'>" } — biggest n
- bottom: { "tool":"bottom", "n":<int>, "by":"<optional col/'value'>" } — smallest n

TERMINAL STEPS (produce the final answer; use at most one per plan)

- average / sum / maximum / minimum / median / mode: { "tool":"average", "measure":"<numeric col>" }
- count: { "tool":"count" }
- trend: { "tool":"trend", "dateColumn":"<time col>", "measure":"<numeric col>", "period":"year"|"quarter"|"month"|"day", "aggregation":"sum"|"average"|"count"|"maximum"|"minimum" }
    use for "trend", "over time", "by year/month" style questions
- compare: { "tool":"compare", "column":"<col>", "valueA":<val>, "valueB":<val>, "measure":"<numeric col>", "aggregation":"sum"|"average"|"count"|"maximum"|"minimum", "datePart":"year"|"quarter"|"month"|"day" }
    use for "X vs Y" / "compare A and B" questions. "datePart" is optional,
    same rule as filter — use it when comparing years/months/quarters on a
    full date column instead of a standalone Year/Month column.
- correlation: { "tool":"correlation", "columnX":"<numeric col>", "columnY":"<numeric col>" }
    use for "relationship between X and Y" / "does X affect Y" questions
- chart: { "tool":"chart", "chartType":"bar"|"line"|"pie", "dimension":"<categorical col>", "measure":"<numeric col>", "aggregation":"sum"|"average"|"count"|"maximum"|"minimum", "top":<int, optional, default 8> }
    OR, for a time-series chart instead of "dimension":
    { "tool":"chart", "chartType":"line", "dateColumn":"<time col>", "measure":"<numeric col>", "period":"year"|"quarter"|"month"|"day", "aggregation":"sum"|"average"|"count"|"maximum"|"minimum" }
    Use whenever the user asks to see/show/plot/visualize/chart/graph data,
    or to change the current chart's type or grouping.
    If the user is just changing the chart TYPE ("make it a line chart",
    "show as pie") without specifying new columns, reuse the dimension/
    measure/dateColumn/aggregation from "Current Chart" below and only
    change chartType.
- driver: { "tool":"driver", "measure":"<numeric col>", "dateColumn":"<time col>", "periodA":<optional>, "periodB":<optional>, "dimensions":["<col>", ...] }
    Use for "why did X change/decline/grow", "what's driving/causing this",
    "what explains the change" questions. periodA/periodB are OPTIONAL — if
    omitted, the engine compares the two most recent periods automatically.
    "dimensions" should list 2-4 meaningful categorical columns from
    schema.dimensions/schema.people to investigate as possible contributors
    (e.g. Product, Region, Sales Rep) — never every column, only ones likely
    to matter for this measure.

If the question just wants a filtered/grouped/sorted TABLE (e.g. "top 5
products by revenue") you do NOT need a terminal step — stop after
groupBy/sort/top and the engine will return the resulting table.

Only ever use column names that exist in the schema below. Pick the most
sensible measure/dimension/time columns from schema.measures / .dimensions / .time.

OPTIONAL "presentation" FIELD (top-level, alongside "steps")

You may set { "steps": [...], "presentation": "text" | "chart" | "table" | "chart_table" }
when the wording of the question makes the right output format clear. Leave
it unset if you're not sure — the engine has a sensible default.

Set it based on what the user is actually asking for, not just the tool used:
- "text": the user wants a direct answer, nothing else ("just tell me...",
  "what is...", a simple lookup of one thing).
- "chart": the question is fundamentally about comparison, trend, ranking,
  or relative differences, and exact numbers aren't the point.
- "table": the user wants a list/exact values/detailed breakdown to inspect
  ("show me the top 10...", "list...", "exact numbers").
- "chart_table": the question explicitly calls for a "breakdown" or similar
  where BOTH the relative comparison (chart) and exact values (table) add
  real value — not just because multiple categories exist.
A single-value result (one number, one winner) never gets a chart or table
regardless of this field — the engine enforces that itself.

Dataset Schema

${JSON.stringify(schema, null, 2)}

Current Chart (what's shown on the dashboard right now — use this to fill in
missing details for a "change the chart" follow-up request; null if none yet)

${JSON.stringify(currentChart, null, 2)}

Conversation Context (what was measured/filtered/discussed in the last
analytical turn of this conversation — use this to resolve short follow-up
questions that don't repeat everything, e.g. "what about 2013?", "compare
her with Ashish", "and by region?". Fill in whatever the new question
doesn't restate from this context. If the new question is fully
self-contained, ignore this and plan it fresh.)

${describeContext(conversationContext)}

FOLLOW-UP RESOLUTION EXAMPLES

Context: Measure: Sales, Dimension: Rep, Entities discussed: Jane
Q: "What about 2013?"
{ "steps": [
  { "tool":"filter", "column":"Order Date", "operator":"=", "value":2013, "datePart":"year" },
  { "tool":"groupBy", "dimension":"Rep", "measure":"Sales", "aggregation":"sum" },
  { "tool":"sort", "by":"value", "direction":"desc" },
  { "tool":"top", "n":1 }
]}
(the time period is new, but measure/dimension/intent carry over from context)

Context: Measure: Sales, Entities discussed: Jane
Q: "Compare her with Ashish"
{ "steps": [
  { "tool":"compare", "column":"Rep", "valueA":"Jane", "valueB":"Ashish", "measure":"Sales", "aggregation":"sum" }
]}
("her" resolves to the entity from context)

Response format (JSON only)

{
  "steps": []
}

Examples

Q: "Average Value in 2013"
{ "steps": [
  { "tool":"filter", "column":"Year", "operator":"=", "value":2013 },
  { "tool":"average", "measure":"Value" }
]}
(Use this form only if schema has a literal "Year" column. If instead there is
only a full date column like "Order Date" in schema.time, use datePart:)
{ "steps": [
  { "tool":"filter", "column":"Order Date", "operator":"=", "value":2013, "datePart":"year" },
  { "tool":"average", "measure":"Value" }
]}

Q: "Average sales by region"
{ "steps": [
  { "tool":"groupBy", "dimension":"Region", "measure":"Sales", "aggregation":"average" }
]}

Q: "Top 5 products by revenue"
{ "steps": [
  { "tool":"groupBy", "dimension":"Product", "measure":"Revenue", "aggregation":"sum" },
  { "tool":"sort", "by":"value", "direction":"desc" },
  { "tool":"top", "n":5 }
]}

Q: "What is the sales trend by year?"
{ "steps": [
  { "tool":"trend", "dateColumn":"Date", "measure":"Sales", "period":"year", "aggregation":"sum" }
]}

Q: "Compare sales in 2013 vs 2014"
{ "steps": [
  { "tool":"compare", "column":"Year", "valueA":2013, "valueB":2014, "measure":"Sales", "aggregation":"sum" }
]}

Q: "Is there a relationship between price and quantity sold?"
{ "steps": [
  { "tool":"correlation", "columnX":"Price", "columnY":"Quantity" }
]}

Q: "Show me a bar chart of sales by region"
{ "steps": [
  { "tool":"chart", "chartType":"bar", "dimension":"Region", "measure":"Sales", "aggregation":"sum" }
]}

Q: "Change it to a line chart" (Current Chart shows dimension:"Region", measure:"Sales")
{ "steps": [
  { "tool":"chart", "chartType":"line", "dimension":"Region", "measure":"Sales", "aggregation":"sum" }
]}

Q: "Plot the sales trend over time"
{ "steps": [
  { "tool":"chart", "chartType":"line", "dateColumn":"Date", "measure":"Sales", "period":"year", "aggregation":"sum" }
]}

Q: "Why did sales decline?"
{ "steps": [
  { "tool":"driver", "measure":"Sales", "dateColumn":"Date", "dimensions":["Region","Product"] }
]}

Q: "Who is the top sales representative?" (single winner — text only)
{ "steps": [
  { "tool":"groupBy", "dimension":"Rep", "measure":"Sales", "aggregation":"sum" },
  { "tool":"sort", "by":"value", "direction":"desc" },
  { "tool":"top", "n":1 }
],
  "presentation": "text"
}

Q: "What is the breakdown of Value by Postcode for John?" (breakdown wording -> chart AND table)
{ "steps": [
  { "tool":"filter", "column":"Rep", "operator":"=", "value":"John" },
  { "tool":"groupBy", "dimension":"Postcode", "measure":"Value", "aggregation":"sum" },
  { "tool":"sort", "by":"value", "direction":"desc" }
],
  "presentation": "chart_table"
}

Q: "Show me the top 10 customers" (exact ranked list -> table, not chart)
{ "steps": [
  { "tool":"groupBy", "dimension":"Customer", "measure":"Value", "aggregation":"sum" },
  { "tool":"sort", "by":"value", "direction":"desc" },
  { "tool":"top", "n":10 }
],
  "presentation": "table"
}

Q: "Compare Jane, Ashish, and John" (comparison -> chart)
{ "steps": [
  { "tool":"groupBy", "dimension":"Rep", "measure":"Sales", "aggregation":"sum" }
],
  "presentation": "chart"
}

Question:

${question}
`;

  try {

    const response = await askGroq({
      messages: [
        {
          role: "system",
          content: prompt
        }
      ],
      temperature: 0
    });

    const match = response.match(/\{[\s\S]*\}/);

    if (!match)
      throw new Error("Planner JSON missing");

    const plan = JSON.parse(match[0]);

    // Deterministic safety net: if the question names a year but the LLM's
    // plan doesn't actually filter/trend/compare on it, fix that here
    // rather than silently falling back to an unfiltered answer.
    return applyYearGuard(question, schema, plan);

  }
  catch (err) {

    console.error(err);

    return {
      steps: []
    };

  }

}
